import React, { useState, useEffect } from 'react'
import ReactModal from '../ReactModal'
import { isEmpty } from 'lodash'

import {
  RepeatToggle,
  RepeatEvery,
  RepeatOn,
  RepeatEnd,
} from './components'
import { 
  REPEAT_BOOKING,
  getEveryType,
  getBookingDateTime,
  getEndOnDate,
  getEndOnDateDefault,
  getSelectDate,
  getMonthlyOptions,
  getWeekly,
} from '../../helpers'
import { IMG } from '../../themes'

import {
  StyleRepeatModalHeader,
  StyleRepeatModalContent,
  StyleRepeatModalFooter,
} from './styles'

const RepeatModal = (props) => {
  const {
    isVisible,
    date,
    startTime,
    recurrence,
    language,
    onSubmit,
    onClose,
    getHolidays,
    holidays,
    setIsRepeat,
    isRepeat,
    previousRecurrence,
    setPreviousRecurrence,
    colorTheme
  } = props

  const dayList = [getWeekly(date)]

  const [repeatEveryType, setRepeatEveryType] = useState('week')
  const [repeatEveryOccurrence, setRepeatEveryOccurrence] = useState(1)

  const [repeatOnSelectedWeekly, setRepeatOnSelectedWeekly] = useState(dayList)
  const [repeatOnSelectedMonthly, setRepeatOnSelectedMonthly] = useState(getMonthlyOptions(date, language))
  const defaultToggleOn = Boolean(recurrence && recurrence.until)
  const [isSelectedRepeatEndsOn, setIsSelectedRepeatEndsOn] = useState(defaultToggleOn)
  const [repeatEndsOn, setRepeatEndsOn] = useState(getEndOnDateDefault(date))
  const [repeatEndsAfterOccurrence, setRepeatEndsAfterOccurrence] = useState(13)

  const isShowRepeatOn = repeatEveryType !== 'day' && repeatEveryType !== 'year'

  const setDefaultRecurrence = () => {
    setRepeatEveryOccurrence(1)
    setRepeatEndsAfterOccurrence(13)
    setRepeatEveryType('week')
    setRepeatEndsOn(getEndOnDateDefault(date))
    setRepeatOnSelectedWeekly(dayList)
    setIsRepeat(false)
  }

  const handleSubmit = (recurrenceData, language) => {
    const { isDaily, isWeekly, isMonthly, isYearly } = getEveryType(recurrenceData.repeatEveryType)
    const repeatEndsOnDateText = getBookingDateTime(recurrenceData.repeatEndsOn).slice(0, 10) // YYYY-MM-DD
    const startTimeText = getBookingDateTime(recurrenceData.startTime).slice(11) // HH:mm:ss
    const newUntil = `${repeatEndsOnDateText} ${startTimeText}`

    const repeatEndsParams = {
      ...recurrenceData.isSelectedRepeatEndsOn 
        ? { until: newUntil } 
        : { occurrence: recurrenceData.repeatEndsAfterOccurrence }
    }

    const result = isRepeat 
      ? {
        every: [
          recurrenceData.repeatEveryOccurrence,
          recurrenceData.repeatEveryType,
        ],
        ...isDaily && { ...repeatEndsParams },
        ...isWeekly && {
          select: recurrenceData.repeatOnSelectedWeekly,
          ...repeatEndsParams,
        },
        ...isMonthly && {
          select: [recurrenceData.repeatOnSelectedMonthly.key],
          option: recurrenceData.repeatOnSelectedMonthly,
          ...repeatEndsParams,
        },
        ...isYearly && {
          select: [`${getSelectDate(recurrenceData.startTime)}`],
          ...repeatEndsParams,
        },
        } 
      : null

    if (isRepeat) {
      setPreviousRecurrence({
        ...result,
        isSelectedOn: isSelectedRepeatEndsOn,
        isSelectedAfter: !isSelectedRepeatEndsOn,
      })
      onSubmit(result, language)
    } else {
      setPreviousRecurrence(null)
      onSubmit(result, language)
    }
  }

  const setDefaultWeekView = () => {
    setRepeatOnSelectedWeekly([getWeekly(date)])
  }

  const handleClose = () => {
    if (previousRecurrence) {
      setIsRepeat(true)
      setPreviousRecurrence({...previousRecurrence})
    } else {
      setDefaultRecurrence()
    }

    onClose()
  }

  const resultProps = {
    startTime,
    repeatEveryType,
    repeatEveryOccurrence,
    repeatOnSelectedWeekly,
    repeatOnSelectedMonthly,
    isSelectedRepeatEndsOn,
    repeatEndsOn,
    repeatEndsAfterOccurrence,
  }

  useEffect(() => {
    if (!isEmpty(previousRecurrence)) {
      const until = previousRecurrence.until
        ? getEndOnDate(new Date(previousRecurrence.until))
        : getEndOnDateDefault(date)

      setRepeatEveryType(previousRecurrence.every[1])
      setRepeatEveryOccurrence(previousRecurrence.every[0])
      setRepeatOnSelectedWeekly(previousRecurrence.select)
      setRepeatOnSelectedMonthly(previousRecurrence.option)
      setIsSelectedRepeatEndsOn(Boolean(previousRecurrence.until))
      setRepeatEndsOn(until)
      setRepeatEndsAfterOccurrence(previousRecurrence.occurrence || 13)
    } else {
      setDefaultRecurrence()
    }
  }, [previousRecurrence])

  const isDisabled = !repeatEveryOccurrence || !repeatEndsAfterOccurrence

  return (
    <ReactModal isVisible={isVisible} width={420}>
      <StyleRepeatModalHeader>
        <label className='repeat-header-modal'>{REPEAT_BOOKING.CUSTOM_RECURRENCE[language]}</label>
        <img
          className='pointer'
          src={IMG['close.svg']}
          onClick={handleClose}
          alt='close'
        />
      </StyleRepeatModalHeader>

      <StyleRepeatModalContent>
        <RepeatToggle 
          isRepeat={isRepeat} 
          language={language} 
          setIsRepeat={setIsRepeat}
          colorTheme={colorTheme}
        />

        <div className={isRepeat ? 'active-content' : 'inactive-content'}>
          <RepeatEvery
            language={language}
            repeatEveryType={repeatEveryType}
            repeatEveryOccurrence={repeatEveryOccurrence}
            onSelectedRepeatEveryType={setRepeatEveryType}
            onSelectedRepeatEveryOccurrence={setRepeatEveryOccurrence}
            setDefaultWeekView={setDefaultWeekView}
            colorTheme={colorTheme}
          />

          {
            isShowRepeatOn && (
              <RepeatOn
                date={date}
                language={language}
                repeatEveryType={repeatEveryType}
                repeatOnSelectedWeekly={repeatOnSelectedWeekly}
                repeatOnSelectedMonthly={repeatOnSelectedMonthly}
                onSelectedWeekly={setRepeatOnSelectedWeekly}
                onSelectedMonthly={setRepeatOnSelectedMonthly}
                colorTheme={colorTheme}
              />
            )
          }

          <RepeatEnd
            date={date}
            language={language}
            isSelectedRepeatEndsOn={isSelectedRepeatEndsOn}
            repeatEndsOn={repeatEndsOn}
            repeatEndsAfterOccurrence={repeatEndsAfterOccurrence}
            onUpdateIsSelectedRepeatEndsOn={setIsSelectedRepeatEndsOn}
            onEndsOn={setRepeatEndsOn}
            onEndsAfterOccurrence={setRepeatEndsAfterOccurrence}
            getHolidays={getHolidays}
            holidays={holidays}
            colorTheme={colorTheme}
          />
        </div>
      </StyleRepeatModalContent>

      <StyleRepeatModalFooter colorTheme={colorTheme}>
        <button
          className={`submit-button ${isDisabled && 'disabled'}`}
          disabled={isDisabled}
          onClick={() => handleSubmit(resultProps, language)}
        >
          {REPEAT_BOOKING.DONE[language]}
        </button>
      </StyleRepeatModalFooter>
    </ReactModal>
  )
}

export default RepeatModal
